import { createContext, useCallback, useContext } from 'react';
import { useSyncExternalStore } from 'react';

// Phase 1 -- e.g. "decision:ai_search:visibility". See useSharedState.
export const ConversationScopeContext = createContext('');

// Milestone 36 (Talk to Martian: Intelligence-First Chat UX), Section 12 --
// fixes the real bug M34's audit named: DashboardLayout.js renders the
// SAME `rightPanel` element (built once in SahibBriefingPage.js, real
// filename, predates the Martian rebrand) in two
// separate places in the tree -- a desktop `<aside>` and a mobile
// `<Drawer>` (see DashboardLayout.js's own comments) -- and React mounts
// each occurrence as an independent component instance with its own
// hook state. Before this file existed, TalkToMartianPanel's own internal
// `useState` calls meant opening the drawer on mobile showed a
// completely separate, empty conversation from the one already open on
// desktop, and switching viewport width silently abandoned whatever was
// mid-conversation.
//
// This is a MINIMAL, single-purpose external store, not a general state
// management library: one Map entry per real clientId, read/written by
// whichever component instances happen to call useSharedState with that
// SAME clientId. State lives only as long as at least one mounted
// instance references it, and is reset the moment the app reloads --
// there is no persistence layer here, and none is intended (Section 12:
// "refresh behavior should follow the existing intentional refresh
// semantics", i.e. unchanged from before this file existed).
//
// Explicitly NOT an authorization mechanism (Section 12's own hard
// rule): this store only ever decides what the SAME already-authorized
// conversation renders as on screen. Every real backend call a consumer
// of this hook makes still passes its own explicit tenant/client id,
// verified server-side exactly as before -- nothing here can widen or
// substitute for that check.
const stores = new Map();

function getStore(scopeKey) {
  let store = stores.get(scopeKey);
  if (!store) {
    store = { values: new Map(), listeners: new Set() };
    stores.set(scopeKey, store);
  }
  return store;
}

// A `useState`-shaped drop-in: [value, setValue] = useSharedState(scopeKey, key, initial).
// `scopeKey` is the real clientId (or any other real, already-authorized
// identity the caller already scopes its own backend calls to) -- never
// a value this hook invents or derives itself.
export function useSharedState(scopeKey, key, initialValue) {
  // Phase 1 (Founder-First UX): a conversation can belong to one decision.
  // ConversationScope narrows the store WITHIN the same client -- it never
  // replaces the client id, and callers keep sending their own explicit
  // client id to the backend. Absent a scope, behaviour is unchanged.
  const conversationScope = useContext(ConversationScopeContext);
  const base = scopeKey ?? '__unscoped__';
  const store = getStore(conversationScope ? `${base}::${conversationScope}` : base);

  // Lazy init, exactly once per (scopeKey, key) across every mounted
  // instance -- guarded by `.has()` so a second instance mounting later
  // (e.g. the drawer opening after the aside already has a live
  // conversation) reads the EXISTING value instead of resetting it.
  if (!store.values.has(key)) {
    store.values.set(key, typeof initialValue === 'function' ? initialValue() : initialValue);
  }

  const subscribe = useCallback((onStoreChange) => {
    store.listeners.add(onStoreChange);
    return () => store.listeners.delete(onStoreChange);
  }, [store]);

  const getSnapshot = useCallback(() => store.values.get(key), [store, key]);

  const value = useSyncExternalStore(subscribe, getSnapshot);

  const setValue = useCallback((next) => {
    const prev = store.values.get(key);
    const resolved = typeof next === 'function' ? next(prev) : next;
    store.values.set(key, resolved);
    store.listeners.forEach((listener) => listener());
  }, [store, key]);

  return [value, setValue];
}
